import { FormattedMessage, useIntl } from 'react-intl';
import { getUnitTypeLabel } from 'lib/unit_type_utils';
import {
  getSavedTimezone,
  timezoneCode,
} from 'ui_system/utilities/timezoneUtils';
import {
  parseEnglishDateTime,
  parseDateInTimezone,
} from 'ui_system/utilities/dateUtils';

interface RootUnit {
  noun_singular: string;
  noun_plural: string;
}

interface BookingInfoProps {
  date: string;
  time: string;
  duration: string;
  numberOfGuests: number;
  rootUnit: RootUnit;
  online?: boolean;
  tourTimezone?: string;
  isoTime?: boolean;
  locationLabel?: string;
}

export const BookingInfo = ({
  date,
  time,
  duration,
  numberOfGuests,
  rootUnit = {
    noun_singular: 'guest',
    noun_plural: 'guests',
  },
  online,
  tourTimezone,
  isoTime,
  locationLabel,
}: BookingInfoProps) => {
  const intl = useIntl();

  const isOnline = online && tourTimezone;
  const timezone = isOnline ? getSavedTimezone() : undefined;
  const value = isOnline
    ? parseDateInTimezone(date, time, tourTimezone)
    : parseEnglishDateTime(date, time);

  const formattedDate = intl.formatDate(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });
  const formattedTime = intl.formatTime(value, {
    hour: 'numeric',
    minute: '2-digit',
    ...(isoTime && { hourCycle: 'h23' }),
    timeZone: timezone,
  });
  const displayTime = timezone
    ? `${formattedTime} (${timezoneCode(timezone)})`
    : formattedTime;

  return (
    <div className="CheckoutSummary-ContentBox Request-Information">
      <table>
        <tbody>
          {locationLabel && (
            <tr>
              <td className="label">
                <FormattedMessage
                  id="LOCATION_LOCATION"
                  defaultMessage="Location"
                  description="Location"
                />
              </td>
              <td className="value">{locationLabel}</td>
            </tr>
          )}
          <tr>
            <td className="label">
              <FormattedMessage
                id="DATE"
                defaultMessage="Date"
                description="Date"
              />
            </td>
            <td className="value">{formattedDate}</td>
          </tr>
          <tr>
            <td className="label">
              <FormattedMessage defaultMessage="Time" description="Time" />
            </td>
            <td className="value">{displayTime}</td>
          </tr>
          {duration && (
            <tr>
              <td className="label">
                <FormattedMessage
                  id="DURATION"
                  defaultMessage="Duration"
                  description="Duration"
                />
              </td>
              <td className="value">{duration}</td>
            </tr>
          )}
          <tr>
            <td className="label" data-testid="number-of-guests-label">
              <FormattedMessage
                defaultMessage="Number of {rootUnit}"
                description="Number of guests"
                values={{
                  rootUnit: getUnitTypeLabel(
                    intl,
                    rootUnit.noun_singular,
                    rootUnit.noun_plural,
                    numberOfGuests !== 1,
                    { lowercase: true }
                  ),
                }}
              />
            </td>
            <td className="value" data-testid="number-of-guests-value">
              {numberOfGuests}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
